import { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Upload,
  Tag,
  Space,
  Row,
  Col,
  Descriptions,
  InputNumber,
  message,
  Card,
  Grid,
  List,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  DownloadOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getExpenseReports, deleteExpenseReport } from '@/api/expenses';
import { useAuth } from '@/contexts/AuthContext';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface ExpenseItem {
  id: string;
  description: string;
  category: 'utilities' | 'maintenance' | 'security' | 'administrative' | 'other';
  amount: number;
  date: Dayjs;
}

interface ExpenseReport {
  id: string;
  month: number; // 1-12
  year: number;
  title: string;
  summary?: string;
  totalAmount: number;
  fileUrl?: string; // URL to the uploaded report PDF/Excel
  fileName?: string;
  items: ExpenseItem[];
  publishedDate: Dayjs;
}

const expenseItemCategories = {
  utilities: { label: 'Utilities', color: 'blue' },
  maintenance: { label: 'Maintenance', color: 'orange' },
  security: { label: 'Security', color: 'red' },
  administrative: { label: 'Administrative', color: 'purple' },
  other: { label: 'Other', color: 'default' },
};



export default function CommunityExpenses() {
  const { t } = useTranslation(['expenses', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Omit<ExpenseReport, 'id' | 'totalAmount' | 'publishedDate' | 'items'> & { reportMonthYear: Dayjs, items: Array<Omit<ExpenseItem, 'id'>> }>();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<ExpenseReport | null>(null);
  const [viewingReport, setViewingReport] = useState<ExpenseReport | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const screens = Grid.useBreakpoint();

  const [filterYear, setFilterYear] = useState<number | undefined>(dayjs().year());
  const [filterMonth, setFilterMonth] = useState<number | undefined>(undefined);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Query for expense reports
  const { data: expenseData, isLoading } = useQuery({
    queryKey: ['expenseReports', firestoreUser?.workspace?.id, filterYear, filterMonth, pagination.current, pagination.pageSize],
    queryFn: () => getExpenseReports({
      firestoreUser: firestoreUser!,
      pageSize: pagination.pageSize,
      year: filterYear,
      month: filterMonth,
      page: pagination.current
    }),
    enabled: !!firestoreUser?.workspace?.id,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteExpenseReport(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('expenses:reportDeleted'));
      queryClient.invalidateQueries({ queryKey: ['expenseReports'] });
    },
    onError: (error) => {
      message.error(t('common:deleteError'));
      console.error('Error deleting expense report:', error);
    }
  });



  useEffect(() => {
    if (isModalOpen) {
      // Removed unused items variable
    }
  }, [form, isModalOpen]); 

  const handleAddReport = () => {
    setEditingReport(null);
    form.resetFields();
    form.setFieldsValue({
      reportMonthYear: dayjs(), 
      items: [{ category: 'other', date: dayjs() }] 
    });
    setFileList([]);
    setIsModalOpen(true);
  };

  const handleEditReport = (report: ExpenseReport) => {
    setEditingReport(report);
    form.setFieldsValue({
      title: report.title,
      summary: report.summary,
      reportMonthYear: dayjs().month(report.month -1).year(report.year),
      items: report.items.map(item => ({...item, date: dayjs(item.date)})),
    });
    if (report.fileUrl && report.fileName) {
      setFileList([{
        uid: '-1',
        name: report.fileName,
        status: 'done',
        url: report.fileUrl,
      }]);
    } else {
      setFileList([]);
    }
    setIsModalOpen(true);
  };

  const handleDeleteReport = (reportId: string) => {
    deleteMutation.mutate(reportId);
  };
  

  const handleModalOk = async () => {
    try {
      await form.validateFields();
      if (fileList.length > 0 && fileList[0].originFileObj) {
        // In a real app, upload fileList[0].originFileObj to server and get URL
        // For demo, just using a placeholder
        message.info(t('expenses:fileUploadDemoMsg', 'File upload is demo only. File not actually saved to server.'));
      }

      if (editingReport) {
        message.success(t('expenses:reportUpdated', 'Expense report updated successfully'));
        queryClient.invalidateQueries({ queryKey: ['expenseReports'] });
      } else {
        message.success(t('expenses:reportCreated', 'Expense report created successfully'));
        queryClient.invalidateQueries({ queryKey: ['expenseReports'] });
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Validation Failed:', error);
      message.error(t('common:formValidationError'));
    }
  };

  const handleViewReport = (report: ExpenseReport) => {
    setViewingReport(report);
    setIsViewModalOpen(true);
  };

  const uploadProps: UploadProps = {
    onRemove: file => {
      setFileList(prev => prev.filter(f => f.uid !== file.uid));
      return true;
    },
    beforeUpload: file => {
      setFileList([file]); // Allow only single file
      return false; // Prevent auto-upload
    },
    fileList,
    maxCount: 1,
  };

  const reports = expenseData?.reports || [];
  const total = expenseData?.total || 0;

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  const columns = [
    {
      title: t('expenses:table.month'),
      key: 'monthYear',
      render: (_: any, record: ExpenseReport) => `${dayjs().month(record.month - 1).format('MMMM')} ${record.year}`,
      sorter: (a: ExpenseReport, b: ExpenseReport) => a.year - b.year || a.month - b.month,
    },
    {
      title: t('expenses:table.title'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a: ExpenseReport, b: ExpenseReport) => a.title.localeCompare(b.title),
    },
    {
      title: t('expenses:table.totalAmount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => `$${amount.toFixed(2)}`,
      sorter: (a: ExpenseReport, b: ExpenseReport) => a.totalAmount - b.totalAmount,
      align: 'right' as const,
    },
    {
      title: t('expenses:table.publishedDate'),
      dataIndex: 'publishedDate',
      key: 'publishedDate',
      render: (date: Dayjs) => date.format('YYYY-MM-DD'),
      sorter: (a: ExpenseReport, b: ExpenseReport) => a.publishedDate.valueOf() - b.publishedDate.valueOf(),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      align: 'center' as const,
      render: (_: any, record: ExpenseReport) => (
        <Space size="middle">
          <Button icon={<EyeOutlined />} onClick={() => handleViewReport(record)}>{t('common:actions.view')}</Button>
          <Button icon={<EditOutlined />} onClick={() => handleEditReport(record)}>{t('common:actions.edit')}</Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => {
            Modal.confirm({
              title: t('expenses:confirmDeleteReport'),
              content: t('expenses:confirmDeleteReport'),
              okText: t('common:actions.delete'),
              cancelText: t('common:actions.cancel'),
              onOk: () => handleDeleteReport(record.id),
            });
          }}>{t('common:actions.delete')}</Button>
        </Space>
      ),
    },
  ];

  const renderReportCard = (report: ExpenseReport) => (
    <Card
      title={report.title}
      style={{ marginBottom: 16 }}
      actions={[
        <Button type="text" icon={<EyeOutlined />} key="view" onClick={() => handleViewReport(report)}>{t('common:actions.view')}</Button>,
        <Button type="text" icon={<EditOutlined />} key="edit" onClick={() => handleEditReport(report)}>{t('common:actions.edit')}</Button>,
        <Button type="text" danger icon={<DeleteOutlined />} key="delete" onClick={() => {
            Modal.confirm({
                title: t('expenses:confirmDeleteTitle'),
                content: t('expenses:confirmDeleteContent'),
                okText: t('common:actions.delete'),
                cancelText: t('common:actions.cancel'),
                onOk: () => handleDeleteReport(report.id),
            });
          }}>{t('common:actions.delete')}</Button>,
      ]}
    >
      <p><Text strong>{t('expenses:monthYear')}:</Text> {dayjs().month(report.month - 1).format('MMMM')} {report.year}</p>
      <p><Text strong>{t('expenses:totalAmount')}:</Text> ${report.totalAmount.toFixed(2)}</p>
      <p><Text strong>{t('common:publishDate')}:</Text> {report.publishedDate.format('YYYY-MM-DD')}</p>
      {report.fileName && (
        <p><Text strong>{t('expenses:attachedFile')}:</Text> <Tag icon={<PaperClipOutlined />}>{report.fileName}</Tag></p>
      )}
    </Card>
  );

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          {/* Empty Col to push button to the right if title is removed and no other element is on the left */}
        </Col>
        { !screens.xs && (
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddReport}>
              {t('expenses:addReport')}
            </Button>
          </Col>
        )}
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Select 
            placeholder={t('expenses:filterByYear')} 
            onChange={value => {
              setFilterYear(value);
              setPagination({ ...pagination, current: 1 });
            }} 
            style={{ width: '100%' }}
            allowClear
            defaultValue={dayjs().year()}
          >
            {[...new Set(reports.map(r => r.year))].sort((a,b) => b-a).map(year => (
              <Option key={year} value={year}>{year}</Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select 
            placeholder={t('expenses:filterByMonth')} 
            onChange={value => {
              setFilterMonth(value);
              setPagination({ ...pagination, current: 1 });
            }} 
            style={{ width: '100%' }}
            allowClear
          >
            {Array.from({length: 12}, (_, i) => i + 1).map(month => (
              <Option key={month} value={month}>{dayjs().month(month-1).format('MMMM')}</Option>
            ))}
          </Select>
        </Col>
      </Row>

      {screens.xs ? (
        <>
          <Row justify="end" align="middle" style={{ marginBottom: 16, paddingLeft: 8, paddingRight: 8 }}>
            <Col>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddReport} block>
                {t('expenses:addReport')}
              </Button>
            </Col>
          </Row>
          <List
            dataSource={reports}
            renderItem={renderReportCard}
            rowKey="id"
            grid={{ gutter: 16, xs: 1, sm: 1 }}
          />
        </>
      ) : (
        <Table 
          columns={columns} 
          dataSource={reports} 
          rowKey="id" 
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('common:showingRange', { from: range[0], to: range[1], total }),
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize: pageSize || 10 });
            }
          }}
        />
      )}

      {/* Create/Edit Report Modal */}
      <Modal
        title={editingReport ? t('expenses:editReport') : t('expenses:addReport')}
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={() => setIsModalOpen(false)}
        width={800}
        okText={editingReport ? t('common:actions.save') : t('common:actions.create')}
        cancelText={t('common:actions.cancel')}
        destroyOnHidden
        maskClosable={false}
      >
        <Form 
            form={form} 
            layout="vertical" 
            name="expenseReportForm" 
            style={{paddingTop: 16}}
        >
          <Form.Item name="title" label={t('expenses:form.titleLabel')} rules={[{ required: true }]}>
            <Input placeholder={t('expenses:form.titlePlaceholder')}/>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="reportMonthYear" label={t('expenses:form.monthLabel')} rules={[{ required: true }]}>
                <DatePicker picker="month" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
                <Form.Item label={t('expenses:form.yearLabel')} name="year" rules={[{ required: true }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
            </Col>
          </Row>
          <Form.Item name="summary" label={t('expenses:form.summaryLabel')} rules={[{ required: true }]}>
            <TextArea rows={3} placeholder={t('expenses:form.summaryPlaceholder')}/>
          </Form.Item>
          <Form.Item label={t('expenses:form.uploadReport')}>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>{t('common:selectFile')}</Button>
            </Upload>
            {editingReport && editingReport.fileUrl && fileList.length === 0 && (
              <div style={{ marginTop: 8 }}>
                <a href={editingReport.fileUrl} target="_blank" rel="noopener noreferrer">
                  {editingReport.fileName || t('common:viewFile')}
                </a>
              </div>
            )}
          </Form.Item>
          
          <Title level={5} style={{ marginTop: 20, marginBottom: 10 }}>{t('expenses:form.itemsLabel')}</Title>
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} style={{ marginBottom: 16, background: '#f9f9f9' }} size="small">
                    <Row gutter={16}>
                        <Col xs={24} md={10}>
                            <Form.Item
                                {...restField}
                                name={[name, 'description']}
                                label={t('expenses:form.itemDescriptionLabel')}
                                rules={[{ required: true, message: t('common:fieldRequired') }]}
                            >
                                <Input placeholder={t('expenses:form.itemDescriptionPlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item
                                {...restField}
                                name={[name, 'category']}
                                label={t('expenses:form.itemCategoryLabel')}
                                rules={[{ required: true, message: t('common:fieldRequired') }]}
                            >
                                <Select placeholder={t('expenses:form.itemCategoryPlaceholder')}>
                                {Object.entries(expenseItemCategories).map(([catKey, catVal]) => (
                                    <Option key={catKey} value={catKey}>{t(`expenses:categories.${catKey}`, catVal.label)}</Option>
                                ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Item
                                {...restField}
                                name={[name, 'amount']}
                                label={t('expenses:form.itemAmountLabel')}
                                rules={[{ required: true, message: t('common:fieldRequired') }, { type: 'number', min: 0, message: 'Must be positive'}] }
                            >
                                <InputNumber prefix="$" style={{ width: '100%' }} placeholder={t('expenses:form.itemAmountPlaceholder')} />
                            </Form.Item>
                        </Col>
                         <Col xs={24} md={4}>
                             <Form.Item
                                {...restField}
                                name={[name, 'date']}
                                label={t('expenses:form.itemDateLabel')}
                                rules={[{ required: true, message: t('common:fieldRequired') }]}
                            >
                                <DatePicker style={{width: '100%'}}/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Button danger type="link" icon={<DeleteOutlined />} onClick={() => remove(name)} style={{position: 'absolute', top: 8, right: 8}}> 
                        {t('expenses:form.removeItem')}
                    </Button>
                  </Card>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add({ category: 'other', date: dayjs() })} block icon={<PlusOutlined />}>
                    {t('expenses:form.addItem')}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* View Report Modal */}
      {viewingReport && (
        <Modal
          title={viewingReport.title}
          open={isViewModalOpen}
          onCancel={() => setIsViewModalOpen(false)}
          footer={[
            <Button key="close" onClick={() => setIsViewModalOpen(false)}>
              {t('common:actions.close')}
            </Button>,
          ]}
          width={800}
        >
          <Descriptions bordered column={1} size="small" style={{marginBottom: 24}}>
            <Descriptions.Item label={t('expenses:reportMonthYear')}>{dayjs().month(viewingReport.month - 1).year(viewingReport.year).format('MMMM YYYY')}</Descriptions.Item>
            <Descriptions.Item label={t('common:publishDate')}>{viewingReport.publishedDate.format('YYYY-MM-DD')}</Descriptions.Item>
            <Descriptions.Item label={t('expenses:totalAmount')}>{`$${viewingReport.totalAmount.toFixed(2)}`}</Descriptions.Item>
            {viewingReport.summary && <Descriptions.Item label={t('expenses:summaryOptional')}>{viewingReport.summary}</Descriptions.Item>}
            {viewingReport.fileUrl && viewingReport.fileName && (
              <Descriptions.Item label={t('expenses:attachedFile')}>
                <a href={viewingReport.fileUrl} target="_blank" rel="noopener noreferrer" download={viewingReport.fileName}>
                  <Space><PaperClipOutlined />{viewingReport.fileName} <DownloadOutlined /></Space>
                </a>
              </Descriptions.Item>
            )}
          </Descriptions>
          
          <Title level={5} style={{marginBottom: 10}}>{t('expenses:expenseItems')}</Title>
          <Table
            columns={[
              { title: t('common:description'), dataIndex: 'description', key: 'description' },
              { title: t('common:category'), dataIndex: 'category', key: 'category', render: (cat: ExpenseItem['category']) => <Tag color={expenseItemCategories[cat].color}>{t(`expenses:categories.${cat}`, expenseItemCategories[cat].label)}</Tag> },
              { title: t('common:date'), dataIndex: 'date', key: 'date', render: (date: Dayjs) => date.format('YYYY-MM-DD') }, 
              { title: t('expenses:amount'), dataIndex: 'amount', key: 'amount', render: (amount: number) => `$${amount.toFixed(2)}`, align: 'right' as const},
            ]}
            dataSource={viewingReport.items}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Modal>
      )}
    </div>
  );
} 